import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { LOCALES, dict, type LocaleCode, type StringKey } from './strings.js';

/**
 * Locale for the platform console.
 *
 * A React context rather than a store: `session.tsx` already establishes that
 * pattern here, and one mechanism for shared state is easier to follow than two.
 */

const STORAGE_KEY = 'busbook.platform.locale';

function isLocale(value: string | null): value is LocaleCode {
  return !!value && LOCALES.some((l) => l.code === value);
}

/** Remembered choice first, then the browser's preference, then English. */
function initialLocale(): LocaleCode {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* private browsing; fall through to the browser preference */
  }
  for (const tag of navigator.languages ?? []) {
    const base = tag.split('-')[0];
    if (isLocale(base ?? null)) return base as LocaleCode;
  }
  return 'en';
}

export type TFn = (key: StringKey, vars?: Record<string, string | number>) => string;

interface LocaleValue {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: TFn;
}

const Ctx = createContext<LocaleValue | null>(null);

/** `{count}` and friends. Anything unmatched is left visible rather than blanked. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in vars ? String(vars[key]) : whole,
  );
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(initialLocale);

  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* the choice still applies to this session */
    }
    document.documentElement.lang = next;
  }, []);

  const t = useCallback<TFn>(
    (key, vars) => {
      const table = dict[locale] as Record<string, string | undefined>;
      // Per-KEY fallback, not per-locale: a partially translated locale shows the
      // strings it has and English for the rest, instead of reverting wholesale.
      return interpolate(table[key] ?? dict.en[key], vars);
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useLocale must be used inside <LocaleProvider>');
  return value;
}

/** The common case: just the translate function. */
export function useT(): TFn {
  return useLocale().t;
}

/**
 * Chooses singular or plural by count. English, French and Swahili all take the
 * plural for zero here, which reads correctly for a count of outstanding items
 * ("0 invoices awaiting payment"); Lingala follows the same shape.
 */
export function useTPlural(): (base: StringKey, count: number) => string {
  const t = useT();
  return (base, count) =>
    t(count === 1 ? base : (`${base}_other` as StringKey), { count });
}

export { LOCALES } from './strings.js';
export type { LocaleCode, StringKey } from './strings.js';
